import ContentUpload from 'module/ContentUpload/ContentUpload';

import React, { useEffect } from 'react';

import { Alert, Button, Card, Form, Input, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { idCardRequired } from 'utils';
import { useGetArticleContentQuery, useGetCurrentUserQuery, useGetOfferQuery, useSendArticleProcessMutation } from 'services';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiErrorMessage, useForeignAuthorScreenGuard } from 'hooks';

import AddMediaContent from './AddMediaContent';
import MediaActionProvider from './AddMediaContent/Context/MediaActioncontext';

/**
 * @description Yazarın madde dosyasını yükleyip maddeyi gönderdiği ekran.
 *
 * @param {Object} props
 * @param {boolean} [props.onBehalfOfAuthor=false] Madde yazarın kendisi yerine onun adına mı gönderiliyor.
 *   Yabancı yazarlı maddede yazara sistemden bilgilendirme gitmediği için madde dosyasını sorumlu ilim
 *   heyetinin başkanı ya da sekreteri yükleyip gönderir. Bu modda ekranda uyarı basılır, yetkisiz kullanıcı
 *   madde detayına yönlendirilir ve gönderim sonrası "maddelerim" yerine madde detayına dönülür.
 */
const AddNewArticleView = ({ onBehalfOfAuthor = false }) => {
  const { id } = useParams();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { data: current_user } = useGetCurrentUserQuery();
  const { data: offer, isLoading: isOfferLoading } = useGetOfferQuery(id);
  const articleType = offer && offer.articleType;
  const [sendArticleProcess] = useSendArticleProcessMutation();
  const getErrorMessage = useApiErrorMessage();
  /**
   * Route herkese açık olduğu için yazar adına gönderim yetkisi burada denetlenir; yetkisiz kullanıcı
   * madde detayına yönlendirilir. Yazarın kendi gönderiminde (`onBehalfOfAuthor` false) koruma kapalıdır.
   */
  useForeignAuthorScreenGuard(offer, { isLoading: isOfferLoading, enabled: onBehalfOfAuthor });

  const { data } = useGetArticleContentQuery(
    {
      offerID: id,
    },
    {
      skip: !current_user,
    }
  );

  const itemFile = Form.useWatch('itemFile', form);
  const idCard = Form.useWatch('idCard', form);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        itemFile: data.contents.find((item) => item.type === 0),
        idCard: data.contents.find((item) => item.type === 1),
      });
    }
  }, [data, form]);

  /**
   * Bazi Madde tulerinde kimlik karti zorunlulugu bulunmakta bu turler
   * @see idCardRequired arrayinde JSDOC olarak belirtilmiştir
   * @type {boolean} bu constant turlerden birine ait olup olmamasina gore boolean deger doner
   */
  const isIdCardRequired = idCardRequired.includes(articleType && articleType.id);

  // madde gönderme işlemi medya kaydetten tamamen farklı bir işlem
  const handleSendArticle = async (values) => {
    const { error } = await sendArticleProcess({
      id,
      body: {
        opinion: 'approved',
        authorDescription: values.authorDescription,
      },
    });

    if (!error) {
      // Heyetin "maddelerim" sayfası yoktur; yazar adına yapılan gönderimde madde detayına dönülür.
      navigate(onBehalfOfAuthor ? `/madde-detay/${id}` : '/maddelerim');
    } else {
      message.error(getErrorMessage(error));
    }
  };
  useEffect(() => {
    if (document) {
      // buradaki id degeri /src/routes/index.js dosyasindaki div in id degerini ifade ediyor
      const element = document.getElementById('routes-wrapper');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, []);

  /**
   * Yuklenene madde dosyalarinda zorunlu olanlarin hepsi yuklenmeden gonder butonunun aktif olmamasini saglar
   */
  const disableSendButton = Boolean(!itemFile) || (isIdCardRequired && Boolean(!idCard));

  return (
    <>
      {onBehalfOfAuthor && (
        <Alert
          className="gx-mb-3"
          type="warning"
          showIcon
          message="Bu madde yazar adına gönderiliyor"
          description="Yabancı yazara sistemden bilgilendirme gönderilmediği için madde dosyasını sorumlu ilim heyetinin başkanı ya da sekreteri yükleyip gönderir. Yüklediğiniz dosya yazarın madde dosyası olarak yeni bir sürümle kaydedilir, eklediğiniz medya yazarın medyası sayılır. Gönderim madde geçmişine sizin adınızla kaydedilir."
        />
      )}

      <div className="gx-d-flex gx-justify-content-end">
        <Button
          onClick={() => navigate(-1)}
          type="default"
          style={{
            fontSize: 18,
          }}
        >
          <ArrowLeftOutlined />
          {formatMessage({
            id: 'goBack',
          })}
        </Button>
      </div>
      <Form
        id="add-new-article-form"
        layout="vertical"
        onFinish={handleSendArticle}
        form={form}
      >
        <ContentUpload
          id={id}
          articleTypeID={articleType && articleType.id}
        />
        <Card>
          <MediaActionProvider>
            <AddMediaContent offerID={id} />
          </MediaActionProvider>
        </Card>
        <Form.Item
          className="gx-mb-4"
          label="Açıklamalar ve Notlar"
          name="authorDescription"
          rules={[
            {
              min: 50,
              message: 'Açıklama en az 50 karakter olmalıdır',
            },
          ]}
          normalize={(value) => (value.trim() === '' ? undefined : value)}
        >
          <Input.TextArea
            placeholder="Madde açıklaması giriniz"
            rows={5}
            showCount
          />
        </Form.Item>
        <Form.Item>
          <Button
            block
            disabled={disableSendButton}
            htmlType="submit"
            type="primary"
          >
            {formatMessage({
              id: 'article.status.sendItem',
            })}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default AddNewArticleView;
